/* CASE PLANET — landing behaviour. No dependencies. */
(() => {
  'use strict';

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const once = (el, key) => { if (el.dataset[key]) return false; el.dataset[key] = '1'; return true; };

  /* Shared state: currently chosen variant (kept in sync across both pickers + sticky bar) */
  const state = { variant: null };

  /* ---------------- Toast ---------------- */
  let toastTimer;
  function toast(html) {
    const t = $('[data-cp-toast]');
    if (!t) return;
    t.innerHTML = html;
    t.hidden = false;
    requestAnimationFrame(() => t.classList.add('is-on'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      t.classList.remove('is-on');
      setTimeout(() => { t.hidden = true; }, 400);
    }, 4200);
  }

  /* ---------------- Nav ---------------- */
  function initNav() {
    const nav = $('[data-cp-nav]');
    if (!nav || !once(nav, 'cpReady')) return;
    const onScroll = () => nav.classList.toggle('is-scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const btn = $('[data-cp-menu-toggle]', nav);
    const menu = $('[data-cp-menu]', nav);
    if (!btn || !menu) return;
    const set = (open) => {
      menu.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('cp-menu-open', open);
      btn.innerHTML = open
        ? '<span class="cp-visually-hidden">Close menu</span><svg class="cp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
        : '<span class="cp-visually-hidden">Menu</span><svg class="cp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M4 8h16M4 16h16"/></svg>';
    };
    btn.addEventListener('click', () => set(menu.hidden));
    $$('[data-cp-menu-link]', menu).forEach(a => a.addEventListener('click', () => set(false)));
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !menu.hidden) set(false); });
  }

  /* ---------------- Hero intro ---------------- */
  function initHero() {
    const hero = $('.cp-hero');
    if (!hero) return;
    const run = () => requestAnimationFrame(() => hero.classList.add('is-in'));
    // While the brand intro is playing, hold the hero entrance until the curtain lifts.
    if (document.documentElement.classList.contains('cp-intro-on')) {
      document.addEventListener('cp:intro-reveal', run, { once: true });
    } else {
      run();
    }
  }

  /* ---------------- Brand intro (once per visit; CSS-timed, see "Intro" in the stylesheet) ---------------- */
  function initIntro() {
    const el = $('[data-cp-intro]');
    if (!el) return;
    const html = document.documentElement;
    if (!html.classList.contains('cp-intro-on')) { el.remove(); return; }

    let revealed = false, finished = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      document.dispatchEvent(new Event('cp:intro-reveal'));
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      reveal();
      el.remove();
      html.classList.remove('cp-intro-on');
    };
    const skip = () => {
      if (el.classList.contains('is-skipped')) return;
      el.classList.add('is-skipped');
      reveal();
    };

    el.addEventListener('animationend', e => {
      if (e.target === el && /^cp-intro-out/.test(e.animationName)) finish();
    });
    el.addEventListener('click', skip);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') skip(); }, { once: true });
    setTimeout(reveal, 2200);   // hero entrance starts as the curtain begins to lift
    setTimeout(finish, 4200);   // failsafe
  }

  /* ---------------- Videos: lazy load + play only while visible ---------------- */
  function initVideos(scope = document) {
    const vids = $$('video', scope).filter(v => once(v, 'cpVid'));
    vids.forEach(v => { v.muted = true; });
    if (!('IntersectionObserver' in window)) {
      vids.forEach(v => { if (v.dataset.src) v.src = v.dataset.src; });
      return;
    }
    const io = new IntersectionObserver(entries => {
      entries.forEach(({ target: v, isIntersecting }) => {
        if (isIntersecting) {
          if (v.dataset.src && !v.getAttribute('src')) { v.src = v.dataset.src; v.load(); }
          if (v.hasAttribute('data-cp-autoplay') && !reduceMotion) {
            const p = v.play(); if (p && p.catch) p.catch(() => {});
          }
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '250px 0px' });
    vids.forEach(v => io.observe(v));

    // Reduced motion: don't autoplay the hero either
    if (reduceMotion) $$('video[autoplay]', scope).forEach(v => { v.removeAttribute('autoplay'); v.pause(); });
  }

  /* ---------------- Variant picker ---------------- */
  const pickers = [];

  function initPicker(form) {
    if (!once(form, 'cpReady')) return;
    const data = JSON.parse($('[data-cp-variants]', form).textContent);
    const V = data.variants;
    const names = (data.options || []).map(o => String(o).toLowerCase());

    let bi = names.indexOf(String(data.brandOption).toLowerCase());
    let mi = names.indexOf(String(data.modelOption).toLowerCase());
    if (names.length < 2) { bi = -1; mi = 0; }
    else { if (bi < 0) bi = 0; if (mi < 0 || mi === bi) mi = bi === 0 ? 1 : 0; }

    const section = form.closest('.cp-buy') || document;
    const brandSel = $('[data-cp-brand]', form);
    const modelSel = $('[data-cp-model]', form);
    const brandField = $('[data-cp-brand-field]', form);
    const idInput = $('[data-cp-id]', form);
    const msg = $('[data-cp-msg]', form);
    const addBtn = $('[data-cp-add]', form);
    const addText = $('[data-cp-add-text]', form);
    const buyBtn = $('[data-cp-buynow]', form);

    const option = (value, label, disabled) => {
      const o = document.createElement('option');
      o.value = value; o.textContent = label; if (disabled) o.disabled = true;
      return o;
    };

    function fillBrands() {
      if (!brandSel) return;
      // Case Planet currently sells iPhone covers only.
      // The product has a single "Model" variant option, so "iPhone" is a
      // presentation-only brand selector and does not need a Shopify option.
      if (bi < 0) {
        brandSel.replaceChildren(option('', 'Select brand'), option('__iphone__', 'iPhone'));
        brandSel.disabled = false;
        return;
      }
      const brands = [...new Set(V.map(v => v.opts[bi]))];
      const actualBrand = brands.find(b => /apple|iphone/i.test(String(b))) || brands[0];
      brandSel.replaceChildren(option('', 'Select brand'), ...(actualBrand ? [option(actualBrand, 'iPhone')] : []));
    }

    function fillModels(brand) {
      if (!modelSel) return;
      const list = V.filter(v => bi < 0 || v.opts[bi] === brand);
      if (bi >= 0 && !brand) {
        modelSel.replaceChildren(option('', 'Select brand first'));
        modelSel.disabled = true;
        return;
      }
      modelSel.replaceChildren(
        option('', 'Select model'),
        ...list.map(v => option(v.id, v.available ? v.opts[mi] : `${v.opts[mi]} (sold out)`, !v.available))
      );
      modelSel.disabled = false;
    }

    function render(v) {
      idInput.value = v ? v.id : '';
      const price = v ? v.price : null;
      $$('[data-cp-price]', section).forEach(el => { if (price) el.textContent = price; });
      $$('[data-cp-compare]', section).forEach(el => { el.hidden = !(v && v.compare); if (v && v.compare) el.textContent = v.compare; });
      $$('[data-cp-off]', section).forEach(el => { el.hidden = !(v && v.off > 0); if (v && v.off > 0) el.textContent = `${v.off}% off`; });
      if (addText && addBtn) {
        const base = addBtn.dataset.label;
        const soldOut = v && !v.available;
        addText.textContent = soldOut ? 'Sold out'
          : (addBtn.hasAttribute('data-price-in-label') && price ? `${base} \u2014 ${price}` : base);
        addBtn.disabled = !!soldOut;
        if (buyBtn) buyBtn.disabled = !!soldOut;
      }
      if (v) { msg.hidden = true; form.classList.remove('is-nudged'); }
    }

    function setVariant(v, { broadcast = true, syncSelects = false } = {}) {
      if (syncSelects && v) {
        if (brandSel && bi >= 0) { brandSel.value = v.opts[bi]; fillModels(v.opts[bi]); }
        if (modelSel) modelSel.value = String(v.id);
      }
      render(v);
      if (broadcast) {
        state.variant = v;
        document.dispatchEvent(new CustomEvent('cp:variant', { detail: { v, from: form } }));
        if (v && form.hasAttribute('data-cp-primary')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState(history.state, '', url);
        }
      }
    }

    const deliveryBox = $('[data-cp-delivery]', form);
    const pincodeInput = $('[data-cp-pincode]', form);
    const deliveryCheck = $('[data-cp-check-delivery]', form);
    const deliveryResult = $('[data-cp-delivery-result]', form);

    function addBusinessDays(date, days) {
      const d = new Date(date);
      let left = days;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0) left--;
      }
      return d;
    }

    function formatDate(date) {
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }

    function getDeliveryTier(pin) {
      const prefix = pin.slice(0, 3);
      const tier1 = new Set([
        '110','122','124','125','201','302','380','400','401','410','411','412',
        '500','501','560','561','562','600','601','602','700','712'
      ]);
      const tier2 = new Set([
        '140','143','144','147','160','171','180','181','190','202','203','208',
        '226','229','230','241','243','244','248','250','251','261','262','263',
        '274','275','281','282','283','284','285','286','301','305','306','307',
        '313','314','324','325','332','333','334','341','342','343','360','361',
        '362','363','364','370','390','391','395','396','403','404','405','413',
        '414','415','416','422','423','424','425','431','440','441','442','443',
        '444','445','450','452','453','454','457','458','460','462','464','470',
        '474','475','480','481','482','483','484','485','486','487','491','492',
        '493','494','495','496','497','508','509','515','516','517','518','521',
        '522','523','524','525','526','530','531','532','533','534','535','570',
        '571','572','573','574','575','576','577','580','581','582','583','584',
        '585','586','587','588','589','590','591','593','594','595','596','598',
        '603','604','605','606','607','608','609','610','611','612','613','614',
        '620','621','622','623','624','625','626','627','628','629','630','631',
        '632','641','642','643','644','645','646','647','650','651','652','653',
        '671','673','674','676','678','680','682','683','685','686','688','690',
        '691','692','693','695','696','697','698','721','722','723','724','725',
        '731','732','733','734','735','736','737','741','742','743','744','745',
        '751','752','753','754','755','756','757','758','759','760','761','762',
        '763','764','765','766','767','768','769','781','782','783','784','785',
        '786','787','788','789','790','791','792','793','794','795','796','797',
        '798','799','800','801','802','803','804','805','806','811','812','813',
        '814','815','816','817','821','822','823','824','825','826','827','828',
        '829','841','842','843','844','845','846','847','848','849','851','852',
        '853','854','855','856','857','858','859','860','861','862','863','864',
        '865','866','867','868','869','871','872','873','874','875','876','877',
        '878','879'
      ]);
      if (tier1.has(prefix)) return { name: 'Tier 1', min: 2, max: 4 };
      if (tier2.has(prefix)) return { name: 'Tier 2', min: 3, max: 5 };
      return { name: 'Tier 3 / Other', min: 4, max: 7 };
    }

    function checkDelivery() {
      if (!deliveryResult || !pincodeInput) return;
      const pin = pincodeInput.value.replace(/\D/g, '').slice(0, 6);
      pincodeInput.value = pin;
      if (!/^\d{6}$/.test(pin)) {
        deliveryResult.hidden = false;
        deliveryResult.className = 'cp-delivery__result is-error';
        deliveryResult.textContent = 'Please enter a valid 6 digit pincode.';
        return;
      }
      const tier = getDeliveryTier(pin);
      const start = addBusinessDays(new Date(), tier.min);
      const end = addBusinessDays(new Date(), tier.max);
      deliveryResult.hidden = false;
      deliveryResult.className = 'cp-delivery__result';
      deliveryResult.innerHTML = '<strong>Estimated delivery: ' + formatDate(start) + ' – ' + formatDate(end) + '</strong><span>' + tier.name + ' destination · Dispatched from Pune</span>';
    }

    pincodeInput && pincodeInput.addEventListener('input', () => {
      pincodeInput.value = pincodeInput.value.replace(/\D/g, '').slice(0, 6);
      if (pincodeInput.value.length === 6) checkDelivery();
    });
    deliveryCheck && deliveryCheck.addEventListener('click', checkDelivery);
    pincodeInput && pincodeInput.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); checkDelivery(); }
    });

    brandSel && brandSel.addEventListener('change', () => {
      fillModels(bi < 0 ? null : brandSel.value);
      setVariant(null);
      if (!modelSel.disabled) modelSel.focus();
    });
    modelSel && modelSel.addEventListener('change', () => {
      const v = V.find(x => String(x.id) === modelSel.value) || null;
      setVariant(v);
    });

    function nudge() {
      form.classList.add('is-nudged');
      msg.textContent = (bi >= 0 && brandSel && !brandSel.value) ? 'Choose your phone brand first.' : 'Now choose your phone model.';
      msg.hidden = false;
      const target = (bi >= 0 && brandSel && !brandSel.value) ? brandSel : modelSel;
      if (target) target.focus({ preventScroll: true });
    }

    async function add(buyNow) {
      if (!idInput.value) { nudge(); return; }
      const btn = buyNow ? buyBtn : addBtn;
      btn.classList.add('is-busy');
      try {
        let cart;

        // Prefer Shopify's standard storefront cart action. This keeps cart
        // changes observable to Shopify's standard event/pixel layer, which
        // allows installed analytics integrations to receive a consistent
        // product-added-to-cart signal without adding a second Meta Pixel.
        if (window.Shopify && Shopify.actions && typeof Shopify.actions.updateCart === 'function') {
          const result = await Shopify.actions.updateCart({
            lines: [{ merchandiseId: String(idInput.value), quantity: 1 }],
            event: { context: 'product' }
          });
          if (result?.userErrors?.length) throw new Error(result.userErrors[0].message || 'Could not add to cart.');
          if (result?.warnings?.length && !result.cart) throw new Error(result.warnings[0].message || 'Could not add to cart.');
          cart = result.cart;
        } else {
          // Fallback for older storefront runtimes.
          const res = await fetch(`${ROOT}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: 1 }] })
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.description || json.message || 'Could not add to cart.');
          cart = await (await fetch(`${ROOT}cart.js`)).json();
        }

        if (buyNow) { window.location.href = `${ROOT}checkout`; return; }

        const itemCount = typeof cart?.totalQuantity === 'number'
          ? cart.totalQuantity
          : (cart?.item_count || 0);
        $('[data-cp-cart-count]').forEach(el => {
          el.textContent = itemCount;
          el.hidden = itemCount === 0;
        });
        toast(`<span>Added to cart</span><a href="${ROOT}cart">View cart</a><a href="${ROOT}checkout">Checkout</a>`);
      } catch (err) {
        msg.textContent = err.message;
        msg.hidden = false;
      } finally {
        btn.classList.remove('is-busy');
      }
    }

    form.addEventListener('submit', e => { e.preventDefault(); add(false); });
    buyBtn && buyBtn.addEventListener('click', () => add(true));

    fillBrands();
    fillModels(null);
    const pre = data.selected ? V.find(v => v.id === data.selected) : null;
    if (pre && modelSel) setVariant(pre, { syncSelects: true });
    if (!modelSel) { // product without options
      setVariant(V[0] || null);
    }

    const api = { form, V, sync: v => setVariant(v, { broadcast: false, syncSelects: true }), buyNow: () => add(true), nudge };
    pickers.push(api);
  }

  document.addEventListener('cp:variant', ({ detail }) => {
    pickers.forEach(p => { if (p.form !== detail.from && detail.v && p.V.some(x => x.id === detail.v.id)) p.sync(detail.v); });
    $$('[data-cp-sticky-price]').forEach(el => { if (detail.v) el.textContent = detail.v.price; });
  });

  /* ---------------- Mobile sticky bar ---------------- */
  function initSticky() {
    const bar = $('[data-cp-sticky]');
    const anchor = $('[data-cp-sticky-anchor]');
    if (!bar || !anchor || !once(bar, 'cpReady') || !('IntersectionObserver' in window)) return;
    const btn = $('[data-cp-sticky-btn]', bar);
    let pastMain = false;
    const visiblePickers = new Set();

    const update = () => {
      const show = pastMain && visiblePickers.size === 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      btn.tabIndex = show ? 0 : -1;
    };

    new IntersectionObserver(([e]) => {
      pastMain = !e.isIntersecting && e.boundingClientRect.top < 0;
      update();
    }).observe(anchor);

    // Hide the bar while the second "Ready to spin?" picker is on screen
    const pio = new IntersectionObserver(entries => {
      entries.forEach(e => (e.isIntersecting ? visiblePickers.add(e.target) : visiblePickers.delete(e.target)));
      update();
    });
    // ...and while the final CTA or footer is on screen, so it never covers their buttons
    $$('[data-cp-picker]:not([data-cp-primary]), .cp-final__content .cp-btn, .cp-footer').forEach(f => pio.observe(f));

    btn.addEventListener('click', () => {
      const main = pickers.find(p => p.form.hasAttribute('data-cp-primary')) || pickers[0];
      if (state.variant && state.variant.available && main) { main.buyNow(); return; }
      anchor.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      if (main) setTimeout(main.nudge, 600);
    });
  }

  /* ---------------- Signature scroll-driven spin ---------------- */
  function initSpin(scope = document) {
    $$('[data-cp-spin]', scope).forEach(sec => {
      if (!once(sec, 'cpReady') || reduceMotion) {
        if (reduceMotion) $$('[data-cp-line]', sec).forEach(l => l.classList.add('is-on'));
        return;
      }
      const target = $('[data-cp-spin-target]', sec);
      const lines = $$('[data-cp-line]', sec);
      const meter = $('[data-cp-spin-meter]', sec);
      const turns = parseFloat(sec.dataset.turns) || 1;
      const axis = sec.dataset.axis === 'y' ? 'Y' : 'Z';
      let current = 0, goal = 0, raf = null, active = false;

      const measure = () => {
        const r = sec.getBoundingClientRect();
        const total = r.height - window.innerHeight;
        goal = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
      };
      const tick = () => {
        current += (goal - current) * 0.12;              // eased follow = controlled, weighty spin
        if (Math.abs(goal - current) < 0.0005) current = goal;
        const deg = current * 360 * turns;
        target.style.transform = axis === 'Y' ? `rotateY(${deg}deg)` : `rotate(${deg}deg)`;
        if (meter) meter.style.setProperty('--p', current.toFixed(4));
        lines.forEach((l, i) => l.classList.toggle('is-on', current >= (i / lines.length) * 0.92));
        raf = (active || current !== goal) ? requestAnimationFrame(tick) : null;
      };
      const kick = () => { measure(); if (!raf) raf = requestAnimationFrame(tick); };

      new IntersectionObserver(([e]) => { active = e.isIntersecting; if (active) kick(); }, { rootMargin: '100px 0px' }).observe(sec);
      window.addEventListener('scroll', () => { if (active) kick(); }, { passive: true });
      window.addEventListener('resize', kick);
      kick();
    });
  }


  /* ---------------- 360° viewer: spins by drag, scroll and a slow idle turn ---------------- */
  function initViewer(scope = document) {
    $$('[data-cp-viewer]', scope).forEach(sec => {
      if (!once(sec, 'cpReady')) return;
      const stage = $('[data-cp-viewer-stage]', sec);
      const wheel = $('[data-cp-viewer-wheel]', sec);
      const icon  = $('[data-cp-viewer-icon]', sec);
      if (!stage || !wheel) return;

      const idle = reduceMotion ? 0 : (parseFloat(sec.dataset.idle) || 0);      // °/s constant drift
      const kickPerPx = reduceMotion ? 0 : (parseFloat(sec.dataset.scroll) || 0); // °/s added per px scrolled
      const MAX = 1400;                                                          // °/s cap
      const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

      let angle = 0, vel = 0, raf = 0, last = 0, visible = false;
      let dragging = false, prevA = 0, prevT = 0, dragVel = 0;
      let lastY = window.scrollY;

      const paint = () => {
        const a = angle.toFixed(2);
        wheel.style.transform = `rotate(${a}deg)`;
        if (icon) icon.style.transform = `rotate(${a}deg)`;
      };
      const tick = now => {
        const dt = Math.min(0.05, (now - last) / 1000) || 0.016;
        last = now;
        if (!dragging) {
          vel *= Math.exp(-dt * 2.2);                 // friction: the spin coasts, then settles into the idle turn
          if (Math.abs(vel) < 0.4) vel = 0;
          angle += (idle + vel) * dt;
        }
        paint();
        raf = visible && (idle !== 0 || vel !== 0 || dragging) ? requestAnimationFrame(tick) : 0;
      };
      const kick = () => { if (!raf) { last = performance.now(); raf = requestAnimationFrame(tick); } };

      /* Scroll = a flick to the wheel. Down spins one way, up spins back. */
      const onScroll = () => {
        if (!sec.isConnected) { window.removeEventListener('scroll', onScroll); return; }
        const y = window.scrollY, dy = y - lastY;
        lastY = y;
        if (!visible || !kickPerPx || dragging) return;
        vel = clamp(vel + dy * kickPerPx, -MAX, MAX);
        kick();
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      /* Drag / touch = the wheel follows your finger around its centre, then coasts. */
      const angleAt = e => {
        const r = wheel.getBoundingClientRect();
        return Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180 / Math.PI;
      };
      const endDrag = fling => {
        if (!dragging) return;
        dragging = false;
        stage.classList.remove('is-drag');
        vel = fling && performance.now() - prevT < 90 ? clamp(dragVel, -MAX, MAX) : 0;
        kick();
      };
      stage.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragging = true;
        prevA = angleAt(e); prevT = performance.now(); dragVel = 0; vel = 0;
        stage.classList.add('is-drag');
        try { stage.setPointerCapture(e.pointerId); } catch (_) {}
        kick();
      });
      stage.addEventListener('pointermove', e => {
        if (!dragging) return;
        const a = angleAt(e), t = performance.now();
        let d = a - prevA;
        if (d > 180) d -= 360; else if (d < -180) d += 360;
        angle += d;
        const dt = (t - prevT) / 1000;
        if (dt > 0) dragVel = dragVel * 0.6 + (d / dt) * 0.4;
        prevA = a; prevT = t;
        paint();
      });
      stage.addEventListener('pointerup',     () => endDrag(true));
      stage.addEventListener('pointercancel', () => endDrag(false));
      stage.addEventListener('lostpointercapture', () => endDrag(true));
      stage.addEventListener('keydown', e => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        vel = clamp(vel + (e.key === 'ArrowRight' ? 300 : -300), -MAX, MAX);
        kick();
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => { visible = en.isIntersecting; if (visible) kick(); }, { rootMargin: '120px 0px' }).observe(stage);
      } else { visible = true; kick(); }
    });
  }

  /* ---------------- Back to top ---------------- */
  function initToTop() {
    const btn = $('[data-cp-totop]');
    if (!btn || !once(btn, 'cpReady')) return;
    const upd = () => { btn.hidden = window.scrollY < 700; };
    window.addEventListener('scroll', upd, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
    upd();
  }

  /* ---------------- Reels ---------------- */
  function initReels(scope = document) {
    $$('[data-cp-reel]', scope).forEach(reel => {
      if (!once(reel, 'cpReady')) return;
      const v = $('video', reel);
      const btn = $('[data-cp-reel-toggle]', reel);
      if (!v || !btn) { if (btn) btn.hidden = true; return; }
      btn.addEventListener('click', () => {
        if (v.paused) {
          $$('[data-cp-reel] video').forEach(o => { if (o !== v) { o.pause(); o.closest('[data-cp-reel]').classList.remove('is-playing'); } });
          if (v.dataset.src && !v.getAttribute('src')) v.src = v.dataset.src;
          v.muted = false;
          v.play().catch(() => { v.muted = true; v.play(); });
          reel.classList.add('is-playing');
          btn.setAttribute('aria-label', 'Pause video');
          btn.innerHTML = '<svg class="cp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M8 5.5v13M16 5.5v13"/></svg>';
        } else {
          v.pause();
          reel.classList.remove('is-playing');
          btn.setAttribute('aria-label', 'Play video with sound');
          btn.innerHTML = '<svg class="cp-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>';
        }
      });
    });
  }

  /* ---------------- Carousels: arrows + gallery dots ---------------- */
  function initTracks(scope = document) {
    $$('[data-cp-track]', scope).forEach(track => {
      if (!once(track, 'cpReady')) return;
      const host = track.closest('section') || track.parentElement;
      const step = () => {
        const item = track.firstElementChild;
        return item ? item.getBoundingClientRect().width + 14 : track.clientWidth * 0.8;
      };
      const prev = $('[data-cp-prev]', host), next = $('[data-cp-next]', host);
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

      const dots = $('[data-cp-dots]', host);
      if (dots) {
        const n = track.children.length;
        if (n < 2) return;
        dots.replaceChildren(...Array.from({ length: n }, () => document.createElement('span')));
        const mark = () => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          Array.from(dots.children).forEach((d, j) => d.classList.toggle('is-on', j === i));
        };
        track.addEventListener('scroll', () => requestAnimationFrame(mark), { passive: true });
        mark();
      }
    });
  }

  /* ---------------- Quiet heading reveal ---------------- */
  function initReveal(scope = document) {
    const els = $$('.cp-section .cp-display', scope).filter(el => once(el, 'cpRev'));
    if (!('IntersectionObserver' in window) || reduceMotion) return;
    els.forEach(el => el.setAttribute('data-cp-reveal', ''));
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { threshold: 0.2 });
    els.forEach(el => io.observe(el));
  }

  /* ---------------- Content rises in as it scrolls into view ---------------- */
  function initRise(scope = document) {
    if (!('IntersectionObserver' in window) || reduceMotion) return;
    const sel = '.cp-scheme-dark .cp-lede, .cp-checks li, .cp-review, .cp-feature, .cp-faq__item, .cp-vf__points li';
    const els = $$(sel, scope).filter(el => once(el, 'cpRise'));
    els.forEach(el => {
      el.setAttribute('data-cp-rise', '');
      const idx = Array.prototype.indexOf.call(el.parentElement.children, el);
      el.style.setProperty('--i', Math.min(idx, 5));
    });
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach(el => io.observe(el));
  }

  /* ---------------- Boot ---------------- */
  function boot(scope = document) {
    initNav();
    initVideos(scope);
    $$('[data-cp-picker]', scope).forEach(initPicker);
    initSticky();
    initSpin(scope);
    initViewer(scope);
    initReels(scope);
    initTracks(scope);
    initReveal(scope);
    initRise(scope);
    initToTop();
  }

  const start = () => { initIntro(); initHero(); boot(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  // Theme editor: re-init sections as they are edited
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();

/* ---------- Cart note + discount controls ---------- */
(function(){
  const bootCart=()=>{document.querySelectorAll('[data-cp-note-toggle]').forEach(b=>{if(b.dataset.ready)return;b.dataset.ready='1';b.addEventListener('click',()=>{const p=document.querySelector('[data-cp-note-panel]');if(p)p.hidden=!p.hidden})});document.querySelectorAll('[data-cp-discount-toggle]').forEach(b=>{if(b.dataset.ready)return;b.dataset.ready='1';b.addEventListener('click',()=>{const p=document.querySelector('[data-cp-discount-panel]');if(p)p.hidden=!p.hidden})});document.querySelectorAll('[data-cp-discount-apply]').forEach(b=>{if(b.dataset.ready)return;b.dataset.ready='1';b.addEventListener('click',()=>{const i=document.querySelector('[data-cp-discount-input]'),m=document.querySelector('[data-cp-discount-msg]'),code=i?.value.trim();if(!code)return;if(m){m.hidden=false;m.textContent='Applying discount…'}window.location.href='/discount/'+encodeURIComponent(code)+'?redirect=/cart'})})};if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',bootCart);else bootCart();document.addEventListener('shopify:section:load',bootCart);
})();

/* ---------- Cart drawer close / body state ---------- */
(function(){
  const initCpDrawer=()=>{
    const drawer=document.querySelector('.cp-cart-page');
    const close=document.querySelector('.cp-cart-close');
    if(!drawer)return;
    document.documentElement.classList.add('cp-cart-drawer-open');
    document.body.classList.add('cp-cart-drawer-open');
    if(close && !close.dataset.drawerReady){
      close.dataset.drawerReady='1';
      close.addEventListener('click',e=>{
        e.preventDefault();
        if(window.history.length>1) window.history.back();
        else window.location.href='/';
      });
    }
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',initCpDrawer);else initCpDrawer();
})();
